document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
  const nav = document.querySelector('#SiteNav')
  if (!nav) return

  button.addEventListener('click', () => {
    const isOpen = nav.classList.toggle('is-open')
    button.setAttribute('aria-expanded', String(isOpen))
  })
})

const scrollToTopButton = document.querySelector('[data-scroll-to-top]')

if (scrollToTopButton) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  let scrollTicking = false

  const setScrollToTopState = () => {
    const threshold = Math.max(window.innerHeight * 0.55, 360)
    const isVisible = window.scrollY > threshold

    scrollToTopButton.classList.toggle('is-show', isVisible)
    scrollToTopButton.setAttribute('aria-hidden', String(!isVisible))
    scrollToTopButton.tabIndex = isVisible ? 0 : -1
    scrollTicking = false
  }

  window.addEventListener('scroll', () => {
    if (scrollTicking) return
    scrollTicking = true
    window.requestAnimationFrame(setScrollToTopState)
  }, { passive: true })

  window.addEventListener('resize', setScrollToTopState)

  scrollToTopButton.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    })
  })

  setScrollToTopState()
}

const normalizeImageUrl = (url) => {
  if (!url) return ''
  if (url.startsWith('//')) return `${window.location.protocol}${url}`
  return url
}

const normalizeSrcset = (srcset) => {
  if (!srcset) return ''

  return srcset
    .split(',')
    .map((candidate) => {
      const parts = candidate.trim().split(/\s+/)
      const url = parts.shift()
      if (!url) return ''
      return [normalizeImageUrl(url), ...parts].join(' ')
    })
    .filter(Boolean)
    .join(', ')
}

const recommendationImageWidths = [540, 720, 900, 1100, 1400, 1600]
const recommendationImageSizes = '(min-width: 1120px) 25vw, (min-width: 760px) 33vw, 50vw'

const resizeShopifyImageUrl = (url, width) => {
  const normalizedUrl = normalizeImageUrl(String(url || ''))
  if (!normalizedUrl) return ''

  if (/[?&]width=\d+/.test(normalizedUrl)) {
    return normalizedUrl.replace(/([?&])width=\d+/, `$1width=${width}`)
  }

  return `${normalizedUrl}${normalizedUrl.includes('?') ? '&' : '?'}width=${width}`
}

const buildRecommendationImageSrcset = (url) => {
  if (!url) return ''

  return recommendationImageWidths
    .map((width) => `${escapeProductCardHtml(resizeShopifyImageUrl(url, width))} ${width}w`)
    .join(', ')
}

const swapProductMainImage = (image, src, alt, srcset = '') => {
  if (!image || !src) return

  const nextSrc = normalizeImageUrl(src)
  const nextSrcset = normalizeSrcset(srcset)
  const mediaFrame = image.closest('.product-media')

  mediaFrame?.classList.add('is-changing')

  if (nextSrcset) {
    image.srcset = nextSrcset
  } else {
    image.removeAttribute('srcset')
    image.removeAttribute('sizes')
  }

  image.src = nextSrc
  if (alt) {
    image.alt = alt
  }

  const settle = () => {
    mediaFrame?.classList.remove('is-changing')
  }

  if (image.decode) {
    image.decode().then(settle).catch(settle)
  } else {
    image.addEventListener('load', settle, { once: true })
    window.setTimeout(settle, 700)
  }
}

const syncProductGalleryState = (gallery, activeIndex) => {
  if (!gallery || Number.isNaN(activeIndex)) return

  const thumbs = [...gallery.querySelectorAll('[data-product-thumb]')]
  const dots = [...gallery.querySelectorAll('[data-product-dot]')]

  thumbs.forEach((thumb) => {
    const isActive = Number(thumb.dataset.galleryIndex) === activeIndex
    thumb.classList.toggle('is-active', isActive)
    thumb.setAttribute('aria-current', String(isActive))
  })

  dots.forEach((dot) => {
    const isActive = Number(dot.dataset.galleryIndex) === activeIndex
    dot.classList.toggle('is-active', isActive)
    dot.setAttribute('aria-current', String(isActive))
  })

  const activeThumb = thumbs.find((thumb) => Number(thumb.dataset.galleryIndex) === activeIndex)
  const thumbRail = activeThumb?.closest('.product-thumbs')
  if (activeThumb && thumbRail) {
    const targetLeft = activeThumb.offsetLeft - ((thumbRail.clientWidth - activeThumb.offsetWidth) / 2)
    thumbRail.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' })
  }
}

const syncProductVariantFromGalleryControl = (gallery, control) => {
  const variantColor = control?.dataset.variantColor || ''
  if (!gallery || !variantColor) return

  const productSection = gallery.closest('.product-detail') || document
  const form = productSection.querySelector('[data-product-form]')
  const selector = form?.querySelector('[data-variant-select]')
  if (!form || !selector) return

  const matchingOption = [...selector.options].find((option) => option.dataset.variantColor === variantColor)
  if (!matchingOption) return

  const optionValues = (matchingOption.dataset.options || '').split('||')
  form.querySelectorAll('[data-option-group]').forEach((group) => {
    const optionIndex = Number.parseInt(group.dataset.optionIndex || '0', 10)
    const optionValue = optionValues[optionIndex]
    if (!optionValue) return

    const matchingInput = [...group.querySelectorAll('[data-option-value]')].find((input) => input.value === optionValue)
    if (matchingInput) {
      matchingInput.checked = true
    }

    const selectedLabel = group.querySelector('[data-selected-option]')
    if (selectedLabel) {
      selectedLabel.textContent = matchingInput?.value || 'Choose a color'
    }
  })

  matchingOption.selected = true
  selector.value = matchingOption.value
  selector.dispatchEvent(new CustomEvent('change', {
    bubbles: true,
    detail: {
      preserveGallery: true,
      source: 'product-gallery'
    }
  }))
}

const escapeProductGalleryHtml = (value = '') =>
  String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character])

const readProductGalleryMediaItems = (gallery) => {
  if (!gallery) return []
  if (gallery.__productGalleryMediaItems) return gallery.__productGalleryMediaItems

  const manifest = gallery.querySelector('[data-product-gallery-media-json]')
  if (!manifest?.textContent) {
    gallery.__productGalleryMediaItems = []
    return gallery.__productGalleryMediaItems
  }

  try {
    const items = JSON.parse(manifest.textContent)
    gallery.__productGalleryMediaItems = Array.isArray(items)
      ? items.filter((item) => item?.src && item?.thumbSrc)
      : []
  } catch (error) {
    gallery.__productGalleryMediaItems = []
  }

  return gallery.__productGalleryMediaItems
}

const getProductGalleryMediaForVariant = (gallery, variantColor = '') => {
  const mediaItems = readProductGalleryMediaItems(gallery)
  const normalizedVariantColor = String(variantColor || '')
  if (!mediaItems.length) return []

  if (normalizedVariantColor) {
    const scopedItems = mediaItems.filter((item) => String(item.variantColor || '') === normalizedVariantColor)
    if (scopedItems.length) return scopedItems

    if (mediaItems.some((item) => Boolean(item.variantColor))) {
      return []
    }
  }

  const unscopedItems = mediaItems.filter((item) => !item.variantColor)
  return unscopedItems.length ? unscopedItems : mediaItems
}

const renderProductGalleryControls = (gallery, mediaItems) => {
  const thumbs = gallery?.querySelector('.product-thumbs')
  if (!gallery || !thumbs || !mediaItems.length) return false

  thumbs.innerHTML = mediaItems.map((item, index) => `
    <button
      class="product-thumb${index === 0 ? ' is-active' : ''}"
      type="button"
      data-product-thumb
      data-image-src="${escapeProductGalleryHtml(item.src)}"
      data-image-srcset="${escapeProductGalleryHtml(item.srcset || '')}"
      data-image-alt="${escapeProductGalleryHtml(item.alt || '')}"
      data-gallery-index="${index}"
      data-variant-color="${escapeProductGalleryHtml(item.variantColor || '')}"
      aria-label="Show image ${index + 1}"
      aria-current="${index === 0 ? 'true' : 'false'}"
    >
      <img
        src="${escapeProductGalleryHtml(item.thumbSrc)}"
        alt="${escapeProductGalleryHtml(item.alt || '')}"
        srcset="${escapeProductGalleryHtml(item.thumbSrcset || '')}"
        width="320"
        height="400"
        loading="lazy"
      >
    </button>
  `).join('')

  const pagination = gallery.querySelector('.product-gallery__pagination')
  if (pagination) {
    pagination.innerHTML = mediaItems.map((item, index) => `
      <button
        class="${index === 0 ? 'is-active' : ''}"
        type="button"
        data-product-dot
        data-gallery-index="${index}"
        data-variant-color="${escapeProductGalleryHtml(item.variantColor || '')}"
        aria-label="Show image ${index + 1}"
        aria-current="${index === 0 ? 'true' : 'false'}"
      ></button>
    `).join('')
  }

  return true
}

const setProductGalleryMedia = (gallery, control, options = {}) => {
  const selectedControl = control?.matches('[data-product-dot]')
    ? gallery?.querySelector(`[data-product-thumb][data-gallery-index="${control.dataset.galleryIndex}"]`)
    : control
  const mainImage = gallery?.querySelector('[data-main-product-image]')
  if (!gallery || !selectedControl || !mainImage) return

  swapProductMainImage(
    mainImage,
    selectedControl.dataset.imageSrc,
    selectedControl.dataset.imageAlt,
    selectedControl.dataset.imageSrcset
  )

  syncProductGalleryState(gallery, Number(selectedControl.dataset.galleryIndex))

  if (options.syncVariant) {
    syncProductVariantFromGalleryControl(gallery, selectedControl)
  }
}

const getProductGalleryThumbs = (gallery) =>
  [...(gallery?.querySelectorAll('[data-product-thumb]') || [])].filter((thumb) => !thumb.hidden)

const syncProductGalleryControlsVisibility = (gallery) => {
  const hasMultipleImages = getProductGalleryThumbs(gallery).length > 1
  gallery?.querySelector('.product-gallery__controls')?.toggleAttribute('hidden', !hasMultipleImages)
  gallery?.querySelector('.product-gallery__pagination')?.toggleAttribute('hidden', !hasMultipleImages)
}

const getProductGalleryActiveIndex = (thumbs) => Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')))

const moveProductGallery = (gallery, direction, options = {}) => {
  const thumbs = getProductGalleryThumbs(gallery)
  if (!gallery || thumbs.length < 2) return

  const activeIndex = getProductGalleryActiveIndex(thumbs)
  const nextIndex = (activeIndex + direction + thumbs.length) % thumbs.length
  setProductGalleryMedia(gallery, thumbs[nextIndex], options)
}

const filterProductGalleryByVariant = (gallery, variantColor = '', fallbackMediaItem = null) => {
  if (!gallery) return false

  const normalizedVariantColor = String(variantColor || '')
  const manifestItems = getProductGalleryMediaForVariant(gallery, normalizedVariantColor)
  const galleryItems = manifestItems.length ? manifestItems : fallbackMediaItem?.src ? [fallbackMediaItem] : []
  if (galleryItems.length && renderProductGalleryControls(gallery, galleryItems)) {
    const firstVisibleThumb = getProductGalleryThumbs(gallery)[0]
    if (!firstVisibleThumb) return false

    setProductGalleryMedia(gallery, firstVisibleThumb)
    syncProductGalleryControlsVisibility(gallery)
    return true
  }

  const controls = [...gallery.querySelectorAll('[data-product-thumb], [data-product-dot]')]
  const hasVariantScopedControls = controls.some((control) => Boolean(control.dataset.variantColor || ''))

  controls.forEach((control) => {
    const controlColor = control.dataset.variantColor || ''
    const shouldHide = Boolean(normalizedVariantColor && hasVariantScopedControls && controlColor !== normalizedVariantColor)
    control.hidden = shouldHide

    if (shouldHide) {
      control.classList.remove('is-active')
      control.setAttribute('aria-current', 'false')
    }
  })

  const firstVisibleThumb = getProductGalleryThumbs(gallery)[0]
  if (!firstVisibleThumb) return false

  setProductGalleryMedia(gallery, firstVisibleThumb)
  syncProductGalleryControlsVisibility(gallery)
  return true
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null
  const button = target?.closest('[data-product-thumb], [data-product-dot]')
  if (!button) return

  setProductGalleryMedia(button.closest('[data-product-gallery]'), button, { syncVariant: true })
})

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null
  const button = target?.closest('[data-gallery-prev], [data-gallery-next]')
  if (!button) return

  const gallery = button.closest('[data-product-gallery]')
  const direction = button.matches('[data-gallery-prev]') ? -1 : 1

  moveProductGallery(gallery, direction, { syncVariant: true })
})

document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
  const mediaFrame = gallery.querySelector('[data-product-media-frame]')
  const mainImage = gallery.querySelector('[data-main-product-image]')
  if (!mediaFrame || !mainImage) return

  syncProductGalleryControlsVisibility(gallery)
  const initialActiveThumb = gallery.querySelector('[data-product-thumb].is-active') || getProductGalleryThumbs(gallery)[0]
  if (initialActiveThumb) {
    setProductGalleryMedia(gallery, initialActiveThumb)
  }

  let dragStart = null
  const dragThreshold = 52
  const dragLimit = 34

  const setDragOffset = (deltaX) => {
    const offset = Math.max(-dragLimit, Math.min(dragLimit, deltaX * 0.12))
    mediaFrame.style.setProperty('--gallery-drag-offset', `${offset}px`)
  }

  const clearDrag = (pointerId) => {
    if (pointerId !== undefined && mediaFrame.hasPointerCapture?.(pointerId)) {
      mediaFrame.releasePointerCapture(pointerId)
    }

    dragStart = null
    mediaFrame.classList.remove('is-dragging')
    mediaFrame.style.removeProperty('--gallery-drag-offset')
  }

  mainImage.addEventListener('dragstart', (event) => {
    event.preventDefault()
  })

  mediaFrame.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    if (event.target instanceof Element && event.target.closest('a, button, input, label, select, textarea')) return
    if (getProductGalleryThumbs(gallery).length < 2) return

    dragStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY
    }

    mediaFrame.classList.add('is-dragging')
    mediaFrame.setPointerCapture?.(event.pointerId)
  })

  mediaFrame.addEventListener('pointermove', (event) => {
    if (!dragStart || event.pointerId !== dragStart.id) return

    const deltaX = event.clientX - dragStart.x
    const deltaY = event.clientY - dragStart.y

    if (Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault()
      setDragOffset(deltaX)
    }
  })

  mediaFrame.addEventListener('pointerup', (event) => {
    if (!dragStart || event.pointerId !== dragStart.id) return

    const deltaX = event.clientX - dragStart.x
    const deltaY = event.clientY - dragStart.y
    const shouldChangeImage = Math.abs(deltaX) >= dragThreshold && Math.abs(deltaX) > Math.abs(deltaY) * 1.15
    const direction = deltaX < 0 ? 1 : -1

    clearDrag(event.pointerId)

    if (shouldChangeImage) {
      event.preventDefault()
      moveProductGallery(gallery, direction, { syncVariant: true })
    }
  })

  mediaFrame.addEventListener('pointercancel', (event) => {
    if (!dragStart || event.pointerId !== dragStart.id) return
    clearDrag(event.pointerId)
  })
})

document.querySelectorAll('[data-product-form]').forEach((form) => {
  const selector = form.querySelector('[data-variant-select]')
  const priceTargets = document.querySelectorAll('[data-product-price]')
  const compareTargets = document.querySelectorAll('[data-product-compare]')
  const skuTargets = document.querySelectorAll('[data-product-sku]')
  const rewardTargets = document.querySelectorAll('[data-product-reward]')
  const optionGroups = [...form.querySelectorAll('[data-option-group]')]
  const addToCartButton = form.querySelector('[data-add-to-cart-button]')
  const selectedHatProperty = form.querySelector('[data-line-item-selected-hat]')
  const selectedProductSummary = form.querySelector('[data-selected-product-summary]')
  const selectedProductAdded = form.querySelector('[data-selected-product-added]')
  const selectedProductAddedList = form.querySelector('[data-selected-product-added-list]')
  const selectedProductAddedCount = form.querySelector('[data-selected-product-added-count]')
  const selectedProductActions = form.querySelector('[data-selected-product-actions]')
  const viewCartButton = form.querySelector('[data-view-cart-button]')
  const cartMessage = form.querySelector('[data-product-cart-message]')
  const productHandle = form.dataset.productHandle || ''
  const isFixedPackage = form.dataset.packageMode === 'true'
  const mainImage = document.querySelector('[data-main-product-image]')
  const productGallery = mainImage?.closest('[data-product-gallery]') || document.querySelector('[data-product-gallery]')
  let selectedProductCartQuantity = 0

  const updateOptionLabels = () => {
    optionGroups.forEach((group) => {
      const checked = group.querySelector('[data-option-value]:checked')
      const label = group.querySelector('[data-selected-option]')
      if (checked && label) {
        label.textContent = checked.value
      } else if (label) {
        label.textContent = 'Choose a color'
      }
    })

  }

  const syncSelectedProductSummary = (option) => {
    if (!option) return

    form.classList.remove('is-selection-cleared')
    if (selectedHatProperty) {
      selectedHatProperty.disabled = false
    }

    refreshSelectedSummaryVisibility()
  }

  const escapeCartHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;')

  const refreshSelectedSummaryVisibility = () => {
    if (!selectedProductSummary) return

    selectedProductSummary.hidden = selectedProductCartQuantity === 0
  }

  const setCartMessage = (message = '', isError = false) => {
    if (!cartMessage) return

    cartMessage.textContent = message
    cartMessage.hidden = !message
    cartMessage.classList.toggle('is-error', Boolean(isError))
  }

  const updateCartCounters = (itemCount = 0) => {
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = String(itemCount)
    })

    if (viewCartButton) {
      viewCartButton.textContent = `View cart (${itemCount})`
    }
  }

  const getVariantMeta = (variantId) => {
    const variantOption = selector ? [...selector.options].find((option) => String(option.value) === String(variantId)) : null
    const variantTitle = variantOption?.dataset.variantTitle || ''
    const colorInput = optionGroups[0]
      ? [...optionGroups[0].querySelectorAll('[data-option-value]')].find((input) => input.value === variantTitle)
      : null
    const colorLabel = colorInput?.nextElementSibling?.matches('label') ? colorInput.nextElementSibling : null

    return {
      title: variantTitle,
      price: variantOption?.dataset.price || '',
      primaryColor: colorLabel?.style.getPropertyValue('--swatch-color') || '#d8c2a0',
      secondaryColor: colorLabel?.style.getPropertyValue('--swatch-color-secondary') || colorLabel?.style.getPropertyValue('--swatch-color') || '#d8c2a0'
    }
  }

  const itemMatchesCurrentProduct = (item) => {
    if (!productHandle || !item) return false
    if (item.handle === productHandle) return true

    return String(item.url || '').includes(`/products/${productHandle}`)
  }

  const renderSelectedProductCartItems = (cart) => {
    if (!selectedProductAdded || !selectedProductAddedList || !selectedProductActions) return

    const items = Array.isArray(cart?.items) ? cart.items.filter(itemMatchesCurrentProduct) : []
    selectedProductCartQuantity = items.reduce((total, item) => total + Number(item.quantity || 0), 0)

    updateCartCounters(Number(cart?.item_count || 0))

    if (selectedProductCartQuantity === 0) {
      selectedProductAdded.hidden = true
      selectedProductActions.hidden = true
      selectedProductAddedList.innerHTML = ''
      refreshSelectedSummaryVisibility()
      return
    }

    selectedProductAdded.hidden = false
    selectedProductActions.hidden = false
    if (selectedProductAddedCount) {
      const unitSingular = selectedProductAddedCount.dataset.unitSingular || 'hat'
      const unitPlural = selectedProductAddedCount.dataset.unitPlural || 'hats'
      selectedProductAddedCount.textContent = selectedProductCartQuantity === 1
        ? `1 ${unitSingular}`
        : `${selectedProductCartQuantity} ${unitPlural}`
    }

    selectedProductAddedList.innerHTML = items
      .map((item) => {
        const meta = getVariantMeta(item.variant_id)
        const itemTitle = meta.title || item.variant_title || item.title || 'Selected color'
        const itemKey = item.key || item.id || ''

        return `
          <span class="selected-product-summary__added-item">
            <span class="selected-product-summary__added-swatch" style="--swatch-color: ${escapeCartHtml(meta.primaryColor)}; --swatch-color-secondary: ${escapeCartHtml(meta.secondaryColor)};" aria-hidden="true"></span>
            <span>${escapeCartHtml(itemTitle)} x ${Number(item.quantity || 0)}</span>
            <button class="selected-product-summary__line-remove" type="button" data-remove-cart-item="${escapeCartHtml(itemKey)}" aria-label="Remove ${escapeCartHtml(itemTitle)} from cart">
              <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9 4h6l1 2h4v2H4V6h4l1-2Z" fill="currentColor"></path>
                <path d="M6.5 10h11l-.8 9.1A2.1 2.1 0 0 1 14.6 21H9.4a2.1 2.1 0 0 1-2.1-1.9L6.5 10Z" fill="currentColor"></path>
              </svg>
            </button>
          </span>
        `
      })
      .join('')

    refreshSelectedSummaryVisibility()
  }

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('Cart could not be refreshed.')
    }

    return response.json()
  }

  const refreshCartState = async () => {
    const cart = await fetchCart()
    renderSelectedProductCartItems(cart)
    return cart
  }

  const restoreAddToCartButton = (label = '') => {
    if (!addToCartButton) return

    const option = selector?.options[selector.selectedIndex]
    const quantity = Number.parseInt(form.querySelector('[data-quantity-input]')?.value || '1', 10)
    const isUnavailable = Boolean(option?.disabled)

    addToCartButton.disabled = form.classList.contains('is-selection-cleared') || isUnavailable
    addToCartButton.textContent = label || (
      isUnavailable
        ? 'Unavailable'
        : isFixedPackage
          ? 'Add fixed package to cart'
          : quantity > 1
            ? `Add ${quantity} hats to cart`
            : 'Add to cart'
    )
  }

  const addSelectedProductToCart = async () => {
    if (form.classList.contains('is-selection-cleared')) {
      addToCartButton?.focus()
      setCartMessage('Choose a color before adding this hat.', true)
      return
    }

    const formData = new FormData(form)
    const option = selector?.options[selector.selectedIndex]
    const selectedColor = option?.dataset.variantTitle || option?.textContent.split(' - ')[0]?.trim() || 'Selected color'
    const quantity = Number.parseInt(form.querySelector('[data-quantity-input]')?.value || '1', 10)

    if (addToCartButton) {
      addToCartButton.disabled = true
      addToCartButton.textContent = 'Adding...'
    }

    setCartMessage('')

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      })

      if (!response.ok) {
        let errorMessage = 'This hat could not be added to cart.'
        try {
          const errorPayload = await response.json()
          errorMessage = errorPayload.description || errorPayload.message || errorMessage
        } catch (_error) {}
        throw new Error(errorMessage)
      }

      await refreshCartState()
      setCartMessage(isFixedPackage ? 'Fixed package added. View cart or continue shopping.' : `${selectedColor} added. Choose another color or view cart.`)
      restoreAddToCartButton(isFixedPackage ? 'Package added' : quantity > 1 ? `Added ${quantity} hats` : 'Added to cart')
    } catch (error) {
      setCartMessage(error?.message || 'This hat could not be added to cart.', true)
      restoreAddToCartButton()
    }
  }

  const selectedOptionSignature = () =>
    optionGroups
      .map((group) => group.querySelector('[data-option-value]:checked')?.value)
      .filter(Boolean)
      .join('||')

  const syncVariantFromOptions = () => {
    if (!selector || optionGroups.length === 0) return

    const signature = selectedOptionSignature()
    const matchingOption = [...selector.options].find((option) => option.dataset.options === signature)
    if (!matchingOption) return

    selector.value = matchingOption.value
    selector.dispatchEvent(new Event('change', { bubbles: true }))
  }

  optionGroups.forEach((group) => {
    group.querySelectorAll('[data-option-value]').forEach((input) => {
      input.addEventListener('change', () => {
        updateOptionLabels()
        syncVariantFromOptions()
      })
    })
  })

  updateOptionLabels()

  selectedProductAddedList?.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null
    const removeButton = target?.closest('[data-remove-cart-item]')
    if (!removeButton) return

    const cartItemKey = removeButton.getAttribute('data-remove-cart-item')
    if (!cartItemKey) return

    removeButton.setAttribute('disabled', 'disabled')
    setCartMessage('')

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: cartItemKey,
          quantity: 0
        })
      })

      if (!response.ok) {
        throw new Error('This cart item could not be removed.')
      }

      const cart = await response.json()
      renderSelectedProductCartItems(cart)
      setCartMessage('Removed from cart.')
    } catch (error) {
      removeButton.removeAttribute('disabled')
      setCartMessage(error?.message || 'This cart item could not be removed.', true)
    }
  })

  form.addEventListener('submit', (event) => {
    event.preventDefault()
    addSelectedProductToCart()
  })

  refreshCartState().catch(() => {})

  if (!selector) return

  selector.addEventListener('change', (event) => {
    const option = selector.options[selector.selectedIndex]
    if (!option) return
    const shouldPreserveGallery = Boolean(event.detail?.preserveGallery)

    if (option.dataset.price) {
      priceTargets.forEach((target) => {
        target.textContent = option.dataset.price
      })
    }

    compareTargets.forEach((target) => {
      target.textContent = option.dataset.compare || ''
      target.hidden = !option.dataset.compare
    })

    if (option.dataset.sku) {
      skuTargets.forEach((target) => {
        target.textContent = option.dataset.sku
      })
    }

    if (option.dataset.reward) {
      rewardTargets.forEach((target) => {
        target.textContent = option.dataset.reward
      })
    }

    if (selectedHatProperty && option.dataset.selectedHat) {
      selectedHatProperty.value = option.dataset.selectedHat
    }

    syncSelectedProductSummary(option)

    const fallbackGalleryItem = option.dataset.imageSrc
      ? {
        variantColor: option.dataset.variantColor || '',
        src: option.dataset.imageSrc,
        srcset: option.dataset.imageSrcset || '',
        thumbSrc: resizeShopifyImageUrl(option.dataset.imageSrc, 320),
        thumbSrcset: `${resizeShopifyImageUrl(option.dataset.imageSrc, 320)} 320w`,
        alt: option.dataset.imageAlt || option.dataset.variantTitle || ''
      }
      : null
    const galleryUpdated = shouldPreserveGallery ? true : filterProductGalleryByVariant(productGallery, option.dataset.variantColor || '', fallbackGalleryItem)

    if (!galleryUpdated && mainImage && option.dataset.imageSrc) {
      swapProductMainImage(mainImage, option.dataset.imageSrc, option.dataset.imageAlt || mainImage.alt, option.dataset.imageSrcset)
      document.querySelectorAll('[data-product-thumb], [data-product-dot]').forEach((thumb) => {
        thumb.classList.remove('is-active')
        thumb.setAttribute('aria-current', 'false')
      })
    }

    if (addToCartButton) {
      const selectedQuantity = Number.parseInt(form.querySelector('[data-quantity-input]')?.value || '1', 10)
      addToCartButton.disabled = option.disabled
      addToCartButton.textContent = option.disabled ? 'Unavailable' : selectedQuantity > 1 ? `Add ${selectedQuantity} hats to cart` : 'Add to cart'
    }

    setCartMessage('')
  })
})

document.querySelectorAll('[data-product-form]').forEach((form) => {
  const quantityInput = form.querySelector('[data-quantity-input]')
  const presets = [...form.querySelectorAll('[data-quantity-preset]')]
  const addToCartButton = form.querySelector('[data-add-to-cart-button]')
  if (!quantityInput || presets.length === 0) return

  const syncPresetState = () => {
    const activePreset = presets.find((preset) => preset.dataset.quantityPreset === quantityInput.value) || presets[0]
    presets.forEach((preset) => {
      preset.classList.toggle('is-active', preset === activePreset)
    })

    if (addToCartButton && !addToCartButton.disabled) {
      const selectedQuantity = Number.parseInt(quantityInput.value || '1', 10)
      addToCartButton.textContent = selectedQuantity > 1 ? `Add ${selectedQuantity} hats to cart` : 'Add to cart'
    }
  }

  presets.forEach((preset) => {
    preset.addEventListener('click', () => {
      quantityInput.value = preset.dataset.quantityPreset || '1'
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }))
      syncPresetState()
    })
  })

  quantityInput.addEventListener('input', syncPresetState)
  quantityInput.addEventListener('change', syncPresetState)
  syncPresetState()
})

const shippingMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const getShippingNumber = (value, fallback) => {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

const isBusinessDay = (date) => {
  const day = date.getDay()
  return day !== 0 && day !== 6
}

const addBusinessDays = (date, days) => {
  const nextDate = new Date(date)
  let remainingDays = Math.max(0, days)

  while (remainingDays > 0) {
    nextDate.setDate(nextDate.getDate() + 1)
    if (isBusinessDay(nextDate)) {
      remainingDays -= 1
    }
  }

  return nextDate
}

const formatShippingDate = (date, format = '%b %-d') => {
  const day = date.getDate()
  return format
    .replace('%b', shippingMonthNames[date.getMonth()])
    .replace('%-d', String(day))
    .replace('%d', String(day).padStart(2, '0'))
}

const formatShippingRange = (startDate, endDate, format) => {
  const start = formatShippingDate(startDate, format)
  const end = formatShippingDate(endDate, format)
  return start === end ? start : `${start} - ${end}`
}

document.querySelectorAll('[data-gh-shipping-estimator]').forEach((estimator) => {
  const optionTabs = [...estimator.querySelectorAll('[data-gh-shipping-option]')]
  const orderDateTarget = estimator.querySelector('[data-gh-order-date]')
  const shipDateTarget = estimator.querySelector('[data-gh-ship-date]')
  const deliveryDateTarget = estimator.querySelector('[data-gh-delivery-date]')
  const shippingMethodTarget = estimator.querySelector('[data-gh-shipping-method]')
  const shippingCostTarget = estimator.querySelector('[data-gh-shipping-cost]')
  if (optionTabs.length === 0 || !orderDateTarget || !shipDateTarget || !deliveryDateTarget) return

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const format = estimator.dataset.dateFormat || '%b %-d'
  const processingMin = getShippingNumber(estimator.dataset.processingMin, 1)
  const processingMax = getShippingNumber(estimator.dataset.processingMax, 3)
  const shippingMethods = {
    standard: {
      min: getShippingNumber(estimator.dataset.standardMin, 3),
      max: getShippingNumber(estimator.dataset.standardMax, 5),
      label: estimator.dataset.standardLabel || 'Standard Shipping',
      price: getShippingNumber(estimator.dataset.standardPrice, 0)
    },
    expedited: {
      min: getShippingNumber(estimator.dataset.expeditedMin, 2),
      max: getShippingNumber(estimator.dataset.expeditedMax, 3),
      label: estimator.dataset.expeditedLabel || 'Expedited Shipping',
      price: getShippingNumber(estimator.dataset.expeditedPrice, 1295)
    }
  }

  const getActiveShippingMethod = () => {
    const activeTab = optionTabs.find((tab) => tab.classList.contains('is-active')) || optionTabs[0]
    return activeTab?.dataset.ghShippingOption || estimator.dataset.defaultPackage || 'standard'
  }

  const setActiveShippingMethod = (selectedTab, shouldFocus = false) => {
    optionTabs.forEach((tab) => {
      const isActive = tab === selectedTab
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', String(isActive))
      tab.tabIndex = isActive ? 0 : -1
    })

    if (shouldFocus) {
      selectedTab.focus()
    }

    updateShippingEstimate()
  }

  const updateShippingEstimate = () => {
    const selectedMethod = getActiveShippingMethod()
    const method = shippingMethods[selectedMethod] ? selectedMethod : (estimator.dataset.defaultPackage || 'standard')
    const shippingWindow = shippingMethods[method] || shippingMethods.standard
    const shipStart = addBusinessDays(today, processingMin)
    const shipEnd = addBusinessDays(today, processingMax)
    const deliveryStart = addBusinessDays(shipStart, shippingWindow.min)
    const deliveryEnd = addBusinessDays(shipStart, shippingWindow.max)

    orderDateTarget.textContent = formatShippingDate(today, format)
    shipDateTarget.textContent = formatShippingRange(shipStart, shipEnd, format)
    deliveryDateTarget.textContent = formatShippingRange(deliveryStart, deliveryEnd, format)

    if (shippingMethodTarget) {
      shippingMethodTarget.textContent = shippingWindow.label
    }

    if (shippingCostTarget) {
      shippingCostTarget.textContent = shippingWindow.price === 0
        ? 'FREE'
        : `$${(shippingWindow.price / 100).toFixed(2)}`
    }
  }

  optionTabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      setActiveShippingMethod(tab)
    })

    tab.addEventListener('keydown', (event) => {
      const directionKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
      if (!directionKeys.includes(event.key)) return

      event.preventDefault()
      const lastIndex = optionTabs.length - 1
      let nextIndex = index

      if (event.key === 'ArrowLeft') {
        nextIndex = index === 0 ? lastIndex : index - 1
      } else if (event.key === 'ArrowRight') {
        nextIndex = index === lastIndex ? 0 : index + 1
      } else if (event.key === 'Home') {
        nextIndex = 0
      } else if (event.key === 'End') {
        nextIndex = lastIndex
      }

      setActiveShippingMethod(optionTabs[nextIndex], true)
    })
  })

  updateShippingEstimate()
})

document.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
  const tabButtons = [...tabs.querySelectorAll('[data-product-tab]')]
  const panels = [...tabs.querySelectorAll('[data-product-panel]')]

  const activateTab = (button) => {
    const targetId = button.getAttribute('aria-controls')
    tabButtons.forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab === button))
    })
    panels.forEach((panel) => {
      panel.hidden = panel.id !== targetId
    })
  }

  tabButtons.forEach((button) => {
    button.addEventListener('click', () => activateTab(button))
  })
})

document.querySelectorAll('[data-product-video-card]').forEach((card) => {
  const video = card.querySelector('[data-product-video]')
  const trigger = card.querySelector('[data-product-video-trigger]')
  if (!video || !trigger) return

  const playLabel = trigger.getAttribute('aria-label') || 'Play video'
  const pauseLabel = playLabel.replace(/^Play/, 'Pause')
  const hoverPlaybackAllowed = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    && !window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const syncVideoState = () => {
    const isPlaying = !video.paused && !video.ended
    card.classList.toggle('is-playing', isPlaying)
    trigger.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel)
  }

  const playVideo = () => {
    document.querySelectorAll('[data-product-video]').forEach((otherVideo) => {
      if (otherVideo !== video) otherVideo.pause()
    })
    video.muted = true
    video.play().catch(() => {})
  }

  const pauseVideo = ({ reset = false } = {}) => {
    video.pause()
    if (reset && video.readyState > 0) video.currentTime = 0
  }

  const toggleVideo = () => {
    if (video.paused || video.ended) playVideo()
    else pauseVideo()
  }

  trigger.addEventListener('click', toggleVideo)
  video.addEventListener('click', toggleVideo)
  if (hoverPlaybackAllowed) {
    card.addEventListener('mouseenter', playVideo)
    card.addEventListener('mouseleave', () => pauseVideo({ reset: true }))
  }
  video.addEventListener('play', syncVideoState)
  video.addEventListener('pause', syncVideoState)
  video.addEventListener('ended', syncVideoState)
  syncVideoState()
})

const productZoomModal = document.querySelector('[data-product-zoom-modal]')
const productZoomImage = productZoomModal?.querySelector('[data-product-zoom-image]')
const productZoomClose = productZoomModal?.querySelector('[data-product-zoom-close]')

const closeProductZoom = () => {
  if (!productZoomModal) return
  productZoomModal.hidden = true
  productZoomModal.classList.remove('is-open')
  if (productZoomImage) {
    productZoomImage.removeAttribute('src')
    productZoomImage.removeAttribute('srcset')
    productZoomImage.alt = ''
  }
}

document.querySelectorAll('[data-product-zoom-trigger]').forEach((button) => {
  button.addEventListener('click', () => {
    const gallery = button.closest('.product-gallery')
    const mainImage = gallery?.querySelector('[data-main-product-image]')
    if (!productZoomModal || !productZoomImage || !mainImage) return

    productZoomImage.src = mainImage.currentSrc || mainImage.src
    if (mainImage.srcset) {
      productZoomImage.srcset = mainImage.srcset
    }
    productZoomImage.alt = mainImage.alt || ''
    productZoomModal.hidden = false
    productZoomModal.classList.add('is-open')
    productZoomClose?.focus()
  })
})

productZoomClose?.addEventListener('click', closeProductZoom)

productZoomModal?.addEventListener('click', (event) => {
  if (event.target === productZoomModal) {
    closeProductZoom()
  }
})

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && productZoomModal?.classList.contains('is-open')) {
    closeProductZoom()
  }
})

const recentlyViewedStorageKey = '47again:recent-products:v2'
const retiredProductHandles = new Set([
  '47-again-bucket-hat',
  '47-again-bucket-hat-2-pack',
  '47-again-bucket-hat-3-pack',
  '47-again-family-trio-hat-set'
])

const isActiveRecentlyViewedProduct = (product) => {
  const handle = String(product?.handle || '').trim()
  return Boolean(handle) && !retiredProductHandles.has(handle)
}

const escapeProductCardHtml = (value = '') => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;'
})[character])

const readRecentlyViewedProducts = () => {
  try {
    const storedProducts = window.localStorage.getItem(recentlyViewedStorageKey)
    const products = JSON.parse(storedProducts || '[]')
    if (!Array.isArray(products)) return []

    const activeProducts = products.filter(isActiveRecentlyViewedProduct)
    if (activeProducts.length !== products.length) {
      window.localStorage.setItem(recentlyViewedStorageKey, JSON.stringify(activeProducts))
    }

    return activeProducts
  } catch (_error) {
    return []
  }
}

const writeRecentlyViewedProducts = (products) => {
  try {
    window.localStorage.setItem(recentlyViewedStorageKey, JSON.stringify(products))
  } catch (_error) {}
}

const normalizeRecentlyViewedColor = (value = '') => String(value || '').trim().toLowerCase().replace(/\s+/g, '-')

const inferRecentlyViewedActiveColor = (product) => {
  const explicitColor = normalizeRecentlyViewedColor(product?.activeColor)
  if (explicitColor) return explicitColor

  const titleColor = String(product?.title || '').split(' - ').pop()
  const normalizedTitleColor = normalizeRecentlyViewedColor(titleColor)
  const swatches = Array.isArray(product?.swatches) ? product.swatches : []
  const matchingSwatch = swatches.find((swatch) => normalizeRecentlyViewedColor(swatch?.name) === normalizedTitleColor)

  return matchingSwatch ? normalizedTitleColor : ''
}

const normalizeRecentlyViewedProduct = (product) => ({
  handle: String(product?.handle || ''),
  title: String(product?.title || '47 Again Hat'),
  url: String(product?.url || '/collections/all'),
  type: String(product?.type || 'Snapback Hat'),
  price: String(product?.price || ''),
  compareAtPrice: String(product?.compareAtPrice || (String(product?.price || '').trim() === '$30.00' ? '$59.00' : '')),
  image: String(product?.image || ''),
  imageAlt: String(product?.imageAlt || product?.title || '47 Again hat'),
  copy: String(product?.copy || '47 Again headwear with Shopify checkout.'),
  rewardUnits: String(product?.rewardUnits || '40'),
  available: product?.available !== false,
  activeColor: inferRecentlyViewedActiveColor(product),
  swatches: Array.isArray(product?.swatches)
    ? product.swatches.slice(0, 5).map((swatch) => ({
        name: String(swatch?.name || 'Color'),
        color: String(swatch?.color || '#d8c2a0'),
        secondaryColor: String(swatch?.secondaryColor || swatch?.color || '#d8c2a0'),
        url: String(swatch?.url || '')
      }))
    : []
})

const renderRecentlyViewedCard = (product) => {
  const imageSrc = normalizeImageUrl(product.image)
  const imageSrcset = buildRecommendationImageSrcset(product.image)
  const responsiveImageMarkup = imageSrcset
    ? ` srcset="${imageSrcset}" sizes="${recommendationImageSizes}"`
    : ''
  const imageMarkup = product.image
    ? `<img class="recommendation-card__image recommendation-card__image--primary" src="${escapeProductCardHtml(imageSrc)}"${responsiveImageMarkup} alt="${escapeProductCardHtml(product.imageAlt)}" width="1600" height="1600" loading="lazy">`
    : '<span class="product-placeholder">47</span>'
  const compareMarkup = product.compareAtPrice
    ? `<s class="recommendation-card__compare">${escapeProductCardHtml(product.compareAtPrice)}</s>`
    : ''
  return `
    <article class="recommendation-card" data-recommendation-card data-recently-viewed-card>
      <a class="recommendation-card__media" href="${escapeProductCardHtml(product.url)}" aria-label="${escapeProductCardHtml(product.title)}">
        <span class="recommendation-card__tags">
          <span class="recommendation-card__tag recommendation-card__tag--ship">Viewed</span>
        </span>
        <span class="recommendation-card__image-stack">
          ${imageMarkup}
        </span>
      </a>

      <div class="recommendation-card__content">
        <p class="recommendation-card__type">${escapeProductCardHtml(product.type)}</p>
        <h3 class="recommendation-card__title"><a href="${escapeProductCardHtml(product.url)}">${escapeProductCardHtml(product.title)}</a></h3>
        <p class="recommendation-card__description">${escapeProductCardHtml(product.copy)}</p>
        <div class="recommendation-card__price-row recommendation-card__price-row--sale">
          <p class="recommendation-card__price recommendation-card__price--sale">${escapeProductCardHtml(product.price)}</p>
          ${compareMarkup}
        </div>
        <p class="recommendation-card__bonus">Gift: +${escapeProductCardHtml(product.rewardUnits)} SPC</p>
      </div>
    </article>
  `
}

document.querySelectorAll('[data-related-products]').forEach((section) => {
  const currentProductJson = section.querySelector('[data-current-product-card-json]')
  const recentSection = section.querySelector('[data-recently-viewed-section]')
  const recentGrid = section.querySelector('[data-recently-viewed-grid]')
  const clearButton = section.querySelector('[data-recently-viewed-clear]')
  if (!currentProductJson) return

  let currentProduct

  try {
    currentProduct = normalizeRecentlyViewedProduct(JSON.parse(currentProductJson.textContent || '{}'))
  } catch (_error) {
    return
  }

  const storedProducts = readRecentlyViewedProducts().map(normalizeRecentlyViewedProduct)
  const previousProducts = storedProducts
    .filter((storedProduct) => storedProduct.handle !== currentProduct.handle)
    .slice(0, 5)

  if (recentSection && recentGrid && previousProducts.length) {
    recentGrid.innerHTML = previousProducts.map(renderRecentlyViewedCard).join('')
    recentSection.hidden = false
  }

  clearButton?.addEventListener('click', () => {
    writeRecentlyViewedProducts([])
    if (recentGrid) {
      recentGrid.innerHTML = ''
    }
    if (recentSection) {
      recentSection.hidden = true
    }
  })

  if (currentProduct.handle) {
    const nextProducts = [
      currentProduct,
      ...storedProducts.filter((storedProduct) => storedProduct.handle !== currentProduct.handle)
    ].slice(0, 8)

    writeRecentlyViewedProducts(nextProducts)
  }
})

document.querySelectorAll('[data-collection-recently-viewed-section]').forEach((section) => {
  const recentGrid = section.querySelector('[data-collection-recently-viewed-grid]')
  const clearButton = section.querySelector('[data-collection-recently-viewed-clear]')
  const recentProducts = readRecentlyViewedProducts()
    .map(normalizeRecentlyViewedProduct)
    .slice(0, 5)

  if (recentGrid && recentProducts.length) {
    recentGrid.innerHTML = recentProducts.map(renderRecentlyViewedCard).join('')
    section.hidden = false
  }

  clearButton?.addEventListener('click', () => {
    writeRecentlyViewedProducts([])
    if (recentGrid) {
      recentGrid.innerHTML = ''
    }
    section.hidden = true
  })
})

document.querySelectorAll('[data-collection-filter-root]').forEach((section) => {
  const filterToggle = section.querySelector('[data-collection-filter-toggle]')
  const filterPanel = section.querySelector('[data-collection-filters]')
  const filterClose = section.querySelector('[data-collection-filter-close]')
  const mobileFilters = window.matchMedia('(max-width: 760px)')
  const params = new URLSearchParams(window.location.search)
  const cards = Array.from(section.querySelectorAll('[data-collection-product-card]'))
  const countTarget = section.querySelector('[data-collection-count]')
  const colorLinks = Array.from(section.querySelectorAll('[data-collection-color-link]'))
  const priceForm = section.querySelector('[data-collection-price-form]')
  const minRange = section.querySelector('[data-price-min-range]')
  const maxRange = section.querySelector('[data-price-max-range]')
  const minInput = section.querySelector('[data-price-min-input]')
  const maxInput = section.querySelector('[data-price-max-input]')
  const priceRange = section.querySelector('[data-price-range]')
  const clearPriceButton = section.querySelector('[data-price-clear]')
  const normalizeFilterValue = (value = '') => String(value).trim().toLowerCase().replace(/\s+/g, '-')
  const parsePrice = (value) => {
    const parsedValue = Number.parseFloat(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }
  const maxPriceLimit = Number.parseFloat(maxRange?.max || maxInput?.max || '80')
  const activeColor = normalizeFilterValue(params.get('color') || '')
  let activePriceMin = parsePrice(params.get('filter.v.price.gte') || params.get('price_min'))
  let activePriceMax = parsePrice(params.get('filter.v.price.lte') || params.get('price_max'))

  const setFilterPanelOpen = (isOpen) => {
    if (!filterToggle || !filterPanel) return

    filterPanel.classList.toggle('is-open', isOpen)
    filterToggle.setAttribute('aria-expanded', String(isOpen))
    filterToggle.querySelector('span').textContent = isOpen ? 'Close filters' : 'Filters'
  }

  filterToggle?.addEventListener('click', () => {
    setFilterPanelOpen(filterToggle.getAttribute('aria-expanded') !== 'true')
  })

  filterClose?.addEventListener('click', () => {
    setFilterPanelOpen(false)
    filterToggle?.focus()
  })

  mobileFilters.addEventListener('change', (event) => {
    if (!event.matches) {
      setFilterPanelOpen(false)
    }
  })

  const updatePriceTrack = () => {
    if (!priceRange || !minRange || !maxRange) return

    const minValue = Number.parseFloat(minRange.value || '0')
    const maxValue = Number.parseFloat(maxRange.value || String(maxPriceLimit))
    const from = Math.max(0, Math.min(100, (minValue / maxPriceLimit) * 100))
    const to = Math.max(0, Math.min(100, (maxValue / maxPriceLimit) * 100))
    priceRange.style.setProperty('--from', `${from}%`)
    priceRange.style.setProperty('--to', `${to}%`)
  }

  const setPriceControls = (minValue, maxValue) => {
    const nextMin = minValue ?? 0
    const nextMax = maxValue ?? maxPriceLimit

    if (minRange) minRange.value = String(nextMin)
    if (maxRange) maxRange.value = String(nextMax)
    if (minInput) minInput.value = minValue === null ? '' : String(nextMin)
    if (maxInput) maxInput.value = maxValue === null ? '' : String(nextMax)
    updatePriceTrack()
  }

  const updateVisibleCards = () => {
    let visibleCount = 0

    cards.forEach((card) => {
      const cardColor = normalizeFilterValue(card.dataset.variantColor || '')
      const cardPrice = Number.parseFloat(card.dataset.variantPrice || '0') / 100
      const matchesColor = !activeColor || cardColor === activeColor
      const matchesMin = activePriceMin === null || cardPrice >= activePriceMin
      const matchesMax = activePriceMax === null || cardPrice <= activePriceMax
      const isVisible = matchesColor && matchesMin && matchesMax

      card.hidden = !isVisible
      if (isVisible) {
        visibleCount += 1
      }
    })

    if (countTarget) {
      const noun = visibleCount === 1 ? 'color option' : 'color options'
      countTarget.textContent = `${visibleCount} ${noun}`
    }
  }

  colorLinks.forEach((link) => {
    const linkColor = normalizeFilterValue(link.dataset.colorFilter || '')
    const isActive = linkColor === activeColor
    link.classList.toggle('is-active', isActive)
    if (isActive) {
      link.setAttribute('aria-current', 'page')
    } else if (link.dataset.colorFilter !== undefined) {
      link.removeAttribute('aria-current')
    }
  })

  setPriceControls(activePriceMin, activePriceMax)

  minRange?.addEventListener('input', () => {
    if (!maxRange || !minInput) return
    if (Number.parseFloat(minRange.value) > Number.parseFloat(maxRange.value)) {
      minRange.value = maxRange.value
    }
    minInput.value = minRange.value
    activePriceMin = parsePrice(minInput.value)
    updatePriceTrack()
    updateVisibleCards()
  })

  maxRange?.addEventListener('input', () => {
    if (!minRange || !maxInput) return
    if (Number.parseFloat(maxRange.value) < Number.parseFloat(minRange.value)) {
      maxRange.value = minRange.value
    }
    maxInput.value = maxRange.value
    activePriceMax = parsePrice(maxInput.value)
    updatePriceTrack()
    updateVisibleCards()
  })

  minInput?.addEventListener('input', () => {
    activePriceMin = parsePrice(minInput.value)
    if (minRange) minRange.value = String(activePriceMin ?? 0)
    updatePriceTrack()
    updateVisibleCards()
  })

  maxInput?.addEventListener('input', () => {
    activePriceMax = parsePrice(maxInput.value)
    if (maxRange) maxRange.value = String(activePriceMax ?? maxPriceLimit)
    updatePriceTrack()
    updateVisibleCards()
  })

  priceForm?.addEventListener('submit', (event) => {
    event.preventDefault()

    const nextUrl = new URL(window.location.href)
    const nextMin = parsePrice(minInput?.value)
    const nextMax = parsePrice(maxInput?.value)

    if (nextMin === null || nextMin <= 0) {
      nextUrl.searchParams.delete('filter.v.price.gte')
      nextUrl.searchParams.delete('price_min')
    } else {
      nextUrl.searchParams.set('filter.v.price.gte', String(nextMin))
      nextUrl.searchParams.delete('price_min')
    }

    if (nextMax === null || nextMax >= maxPriceLimit) {
      nextUrl.searchParams.delete('filter.v.price.lte')
      nextUrl.searchParams.delete('price_max')
    } else {
      nextUrl.searchParams.set('filter.v.price.lte', String(nextMax))
      nextUrl.searchParams.delete('price_max')
    }

    window.location.assign(nextUrl.toString())
  })

  clearPriceButton?.addEventListener('click', () => {
    const nextUrl = new URL(window.location.href)
    nextUrl.searchParams.delete('filter.v.price.gte')
    nextUrl.searchParams.delete('filter.v.price.lte')
    nextUrl.searchParams.delete('price_min')
    nextUrl.searchParams.delete('price_max')
    window.location.assign(nextUrl.toString())
  })

  updateVisibleCards()
})

document.querySelectorAll('[data-catalog-filter-root]').forEach((section) => {
  const tabs = Array.from(section.querySelectorAll('[data-catalog-filter]'))
  const grid = section.querySelector('[data-catalog-grid]')
  const cards = Array.from(grid?.querySelectorAll('[data-product-card]') || [])
  const normalizeFilterValue = (value = '') => String(value).trim().toLowerCase().replace(/\s+/g, '-')

  if (!tabs.length || !cards.length) return

  const setActiveTab = (activeTab) => {
    tabs.forEach((tab) => {
      const isActive = tab === activeTab
      tab.classList.toggle('is-active', isActive)
      if (isActive) {
        tab.setAttribute('aria-current', 'true')
      } else {
        tab.removeAttribute('aria-current')
      }
    })
  }

  const cardMatchesTab = (card, tab) => {
    const filterType = tab.dataset.catalogFilter || 'all'

    if (filterType === 'style') {
      return normalizeFilterValue(card.dataset.productStyle || '') === normalizeFilterValue(tab.dataset.catalogStyle || '')
    }

    if (filterType === 'color') {
      const allowedColors = String(tab.dataset.catalogColors || '')
        .split(',')
        .map(normalizeFilterValue)
        .filter(Boolean)
      const cardColor = normalizeFilterValue(card.dataset.variantColor || '')
      return allowedColors.some((allowedColor) =>
        cardColor === allowedColor || cardColor.includes(allowedColor) || allowedColor.includes(cardColor)
      )
    }

    return true
  }

  const applyFilter = (tab) => {
    cards.forEach((card) => {
      const isVisible = cardMatchesTab(card, tab)
      card.hidden = !isVisible
    })

    setActiveTab(tab)
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', (event) => {
      event.preventDefault()
      applyFilter(tab)
    })
  })

  applyFilter(tabs.find((tab) => tab.getAttribute('aria-current') === 'true') || tabs[0])
})

document.querySelectorAll('[data-track-order-form]').forEach((form) => {
  const input = form.querySelector('[data-track-order-input]')
  const submitButton = form.querySelector('[data-track-order-submit]')
  const message = form.querySelector('[data-track-order-message]')

  if (!input || !submitButton || !message) return

  const setMessage = (text = '') => {
    message.textContent = text
    message.hidden = !text
    input.setAttribute('aria-invalid', text ? 'true' : 'false')
  }

  const normalizeTrackingNumber = (value) => {
    const normalized = value.trim().replace(/[\s-]+/g, '').toUpperCase()

    // Some email clients duplicate a selected USPS number when it is copied.
    if (/^\d{40,44}$/.test(normalized) && normalized.length % 2 === 0) {
      const midpoint = normalized.length / 2
      const firstHalf = normalized.slice(0, midpoint)

      if (firstHalf === normalized.slice(midpoint) && /^\d{20,22}$/.test(firstHalf)) {
        return firstHalf
      }
    }

    return normalized
  }

  const getCarrierUrl = (trackingNumber) => {
    if (/^1Z[A-Z0-9]{16}$/i.test(trackingNumber)) {
      return `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`
    }

    if (/^(JD\d{18}|\d{10})$/i.test(trackingNumber)) {
      return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encodeURIComponent(trackingNumber)}`
    }

    if (/^(\d{12}|\d{15})$/.test(trackingNumber)) {
      return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trackingNumber)}`
    }

    if (/^([A-Z]{2}\d{9}US|\d{20,22})$/i.test(trackingNumber)) {
      return `https://tools.usps.com/tracking/?qtc_tLabels1=${encodeURIComponent(trackingNumber)}`
    }

    return null
  }

  input.addEventListener('input', () => setMessage())

  form.addEventListener('submit', (event) => {
    event.preventDefault()

    const trackingNumber = normalizeTrackingNumber(input.value)

    if (!trackingNumber) {
      setMessage('Enter the tracking number from your shipping confirmation email.')
      input.focus()
      return
    }

    if (!/^[A-Z0-9]{8,44}$/.test(trackingNumber)) {
      setMessage('Check the tracking number and try again. Use only the letters and numbers shown in your shipping email.')
      input.focus()
      return
    }

    const carrierUrl = getCarrierUrl(trackingNumber)

    if (!carrierUrl) {
      setMessage('We could not identify that carrier. Check the number or email help@47again.us for tracking assistance.')
      input.focus()
      return
    }

    submitButton.disabled = true
    submitButton.textContent = 'Opening carrier...'
    window.location.assign(carrierUrl)
  })
})
